/**
 * emailOverviewShared — building blocks shared by EmailOverviewPage and
 * TemplatePerformancePage: formatters, the Brevo freshness banner, the KPI
 * strip and the volume chart.
 *
 * Provenance rule (the whole point of the rebuild): every engagement number
 * says where it came from. Sends are ours (email_sending_log); delivered /
 * open / click / bounce / spam / unsub are Brevo's, and are stamped with the
 * poller's last-success time. A missing poll renders as "not yet synced",
 * never as 0%.
 */
import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from "recharts";
import { MetricCard } from "../../help/MetricCard";
import type {
  ProviderFreshness,
  ProviderTotals,
  Rates,
  SeriesPoint,
} from "../../../services/emailAnalyticsApi";

// ── Formatters ───────────────────────────────────────────────────────────────
export const fmtInt = (n: number | string | null | undefined): string =>
  n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString();

export const fmtPct = (n: number | null | undefined, digits = 1): string =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? "—"
    : `${Number(n).toFixed(digits)}%`;

export const fmtWhen = (iso: string | null | undefined): string => {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const fmtAgo = (iso: string | null | undefined): string => {
  if (!iso) return "never";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

/**
 * Chart axis label for a series point's `day`. Guards BOTH a missing value and
 * an unparseable one — `new Date("undefinedT00:00:00")` does not throw, it just
 * produces an Invalid Date that renders as literal text on the axis.
 */
export const dayLabel = (day: string | null | undefined): string => {
  if (!day) return "—";
  const d = new Date(`${String(day).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const fmtDelay = (hours: number | null | undefined): string => {
  if (hours === null || hours === undefined) return "—";
  const h = Number(hours);
  if (h === 0) return "immediately";
  if (h < 24) return `${h} h`;
  const d = h / 24;
  return Number.isInteger(d) ? `day ${d}` : `${d.toFixed(1)} d`;
};

// ── Freshness ────────────────────────────────────────────────────────────────
export function providerIsFresh(p?: ProviderFreshness | null): boolean {
  if (!p?.daily_last_ok_at && !p?.events_last_ok_at) return false;
  const latest = Math.max(
    p.daily_last_ok_at ? new Date(p.daily_last_ok_at).getTime() : 0,
    p.events_last_ok_at ? new Date(p.events_last_ok_at).getTime() : 0,
  );
  // Poller cadence is 15 min (events) / hourly (daily); 3 h of silence is stale.
  return Date.now() - latest < 3 * 60 * 60 * 1000;
}

export const ProvenanceChip: React.FC<{
  provider?: ProviderFreshness | null;
  source: "brevo" | "log";
}> = ({ provider, source }) => {
  if (source === "log") {
    return (
      <Tooltip title="Counted from LifeScribe's own send log (email_sending_log).">
        <Chip size="small" variant="outlined" label="from send log" />
      </Tooltip>
    );
  }
  const fresh = providerIsFresh(provider);
  const when = provider?.daily_last_ok_at || provider?.events_last_ok_at;
  return (
    <Tooltip
      title={
        when
          ? `Engagement figures come from Brevo's statistics API. Last successful sync ${fmtWhen(when)}.`
          : "Brevo statistics have not been synced yet — engagement figures will fill in after the first poll."
      }
    >
      <Chip
        size="small"
        variant="outlined"
        color={fresh ? "success" : when ? "warning" : "default"}
        icon={fresh ? <CloudDoneOutlinedIcon /> : <CloudOffOutlinedIcon />}
        label={when ? `Brevo · ${fmtAgo(when)}` : "Brevo · not synced"}
      />
    </Tooltip>
  );
};

export const FreshnessBanner: React.FC<{ provider?: ProviderFreshness | null }> = ({
  provider,
}) => {
  if (!provider) return null;
  const when = provider.daily_last_ok_at || provider.events_last_ok_at;

  // An in-flight manual sync outranks the staleness warnings below: the operator
  // just asked for this, and telling them the data is stale while their own
  // request is running reads as a failure rather than as progress.
  if (provider.sync_state === "requested" || provider.sync_state === "running") {
    return (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 2 }}>
        {provider.sync_state === "requested"
          ? "Brevo sync requested — the email service picks it up within a minute."
          : "Syncing with Brevo now."}{" "}
        Engagement figures below still show the last completed sync
        {when ? ` (${fmtWhen(when)})` : ""}; sends are live throughout.
      </Alert>
    );
  }

  if (provider.last_error && !providerIsFresh(provider)) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Brevo sync is failing (<code>{provider.last_error}</code>). Engagement
        figures below are as of {fmtWhen(when)}; sends are still live.
      </Alert>
    );
  }
  if (!when) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Brevo statistics have not been synced yet. Send counts are live;
        delivered / open / click figures will appear after the first poll
        (every 15 minutes).
      </Alert>
    );
  }
  return null;
};

// ── KPI strip ────────────────────────────────────────────────────────────────
type KpiSummary = { sends: number; failed?: number } & ProviderTotals & Rates;

export type KpiCard = { kpi: string; value: string; source: "log" | "brevo"; sub?: string };

/**
 * The seven measures, built from any summary-shaped object.
 *
 * Extracted so the per-funnel strips render the identical set from a
 * SequenceRow rather than a second, drifting copy of the same list — the
 * sequences endpoint already returns every field this reads.
 */
export function buildKpiCards(
  summary: KpiSummary | null | undefined,
  provider: ProviderFreshness | null | undefined,
): KpiCard[] {
  const synced = Boolean(provider?.daily_last_ok_at || provider?.events_last_ok_at);
  const has = (summary?.requests ?? 0) > 0;
  const brevo = (v: string) => (synced && has ? v : "—");
  return [
    {
      kpi: "email.total_sent",
      value: fmtInt(summary?.sends),
      source: "log",
      sub: summary?.failed ? `${fmtInt(summary.failed)} failed` : undefined,
    },
    {
      kpi: "email.delivered_rate",
      value: brevo(fmtPct(summary?.delivery_rate)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.delivered)} of ${fmtInt(summary?.requests)}` : undefined,
    },
    {
      kpi: "email.open_rate",
      value: brevo(fmtPct(summary?.open_rate)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.unique_opens)} unique` : undefined,
    },
    {
      kpi: "email.click_rate",
      value: brevo(fmtPct(summary?.click_rate)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.unique_clicks)} unique` : undefined,
    },
    {
      kpi: "email.bounce_rate.hard",
      value: brevo(fmtPct(summary?.hard_bounce_rate, 2)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.hard_bounces)} hard · ${fmtInt(summary?.soft_bounces)} soft` : undefined,
    },
    {
      kpi: "email.complaint_rate",
      value: brevo(fmtPct(summary?.spam_rate, 2)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.spam_reports)} reports` : undefined,
    },
    {
      kpi: "email.unsubscribe_rate",
      value: brevo(fmtPct(summary?.unsub_rate, 2)),
      source: "brevo",
      sub: has ? `${fmtInt(summary?.unsubscribed)} unsubscribed` : undefined,
    },
  ];
}

export const KpiStrip: React.FC<{
  summary?: KpiSummary | null;
  provider?: ProviderFreshness | null;
  loading?: boolean;
  /** Denser layout for the per-funnel strips nested inside the Funnels panel. */
  dense?: boolean;
  /**
   * Cards prepended before the seven standard measures. Used by the per-funnel
   * strips for enrolments, which only exists per funnel — sends follow
   * enrolments, so it belongs first rather than as an afterthought.
   */
  leadingCards?: KpiCard[];
}> = ({ summary, provider, loading, dense, leadingCards }) => {
  const cards = [...(leadingCards || []), ...buildKpiCards(summary, provider)];
  return (
    <Grid container spacing={dense ? 1 : 2}>
      {cards.map((c) => (
        <Grid key={c.kpi} size={{ xs: 12, sm: 6, md: 4, lg: 12 / 7 }}>
          {loading ? (
            <Skeleton variant="rounded" height={dense ? 88 : 112} />
          ) : (
            <Box>
              <MetricCard kpi={c.kpi} value={c.value} comparison={c.sub} />
              <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>
                <ProvenanceChip provider={provider} source={c.source} />
              </Box>
            </Box>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

// ── Volume chart ─────────────────────────────────────────────────────────────
export const VolumeChart: React.FC<{
  series?: SeriesPoint[] | null;
  loading?: boolean;
  accent?: string;
  height?: number;
}> = ({ series, loading, accent = "#B8860B", height = 260 }) => {
  if (loading) return <Skeleton variant="rounded" height={height} />;
  const rows = (series || []).map((p) => ({
    ...p,
    // slice(0,10): the API historically shipped 'YYYY-MM-DD HH:MM:SS' — parse
    // the date part only so a format drift can never render "Invalid Date".
    // A MISSING day used to slip through as String(undefined) → the axis label
    // read "undefinedT00:00:00" → "Invalid Date"; it now reads '—'.
    label: dayLabel(p.day),
  }));
  const any = rows.some((r) => r.sends || r.requests || r.delivered || r.opens);
  if (!any) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">No sends in this period.</Typography>
      </Box>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
        <ChartTooltip
          formatter={(v: any, name: any) => [fmtInt(v as number), String(name)]}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="sends" name="Sent (our log)" fill={accent} fillOpacity={0.35} radius={[3, 3, 0, 0]} />
        <Area dataKey="delivered" name="Delivered (Brevo)" type="monotone" stroke={accent} fill={accent} fillOpacity={0.12} />
        <Line dataKey="opens" name="Unique opens (Brevo)" type="monotone" stroke="#2E7D32" dot={false} strokeWidth={2} />
        <Line dataKey="clicks" name="Unique clicks (Brevo)" type="monotone" stroke="#1565C0" dot={false} strokeWidth={2} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// ── Small helpers ────────────────────────────────────────────────────────────
export const SectionHeader: React.FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}> = ({ title, subtitle, right }) => (
  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.5 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    {right}
  </Stack>
);

export const ErrorPanel: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <Alert
    severity="error"
    sx={{ my: 2 }}
    action={
      <Chip label="Retry" onClick={onRetry} size="small" color="error" variant="outlined" />
    }
  >
    <strong>Couldn't load this panel.</strong> {message}
  </Alert>
);
